import {
  HelpCircle,
  Shield,
  Lightbulb,
  FileText,
  Users,
  CheckCircle,
  Sparkles,
} from 'lucide-react'
import type { SourceScores, SourceType } from '@/types/interviewer'

interface ScoreDisplayProps {
  scores: SourceScores
  sourceType?: SourceType
}

const SCORE_CONFIG = {
  momTestScore: {
    label: 'Mom Test Score',
    icon: HelpCircle,
    description: 'Interview quality',
    criteria: {
      5: 'Only past behavior, no hypotheticals',
      4: 'Mostly past behavior',
      3: 'Mix of past/future',
      2: 'Mostly hypotheticals',
      1: 'Leading questions',
    },
  },
  evidenceStrength: {
    label: 'Evidence Strength',
    icon: Shield,
    description: 'Validation quality',
    criteria: {
      5: 'Paid money/time already',
      4: 'Active workarounds',
      3: 'Clear frustration',
      2: 'Acknowledges problem',
      1: 'Generic agreement',
    },
  },
  contextRichness: {
    label: 'Context Richness',
    icon: Sparkles,
    description: 'Detail & context',
    criteria: {
      5: 'Rich context and detail',
      4: 'Good context provided',
      3: 'Adequate context',
      2: 'Limited context',
      1: 'Missing context',
    },
  },
  rationaleQuality: {
    label: 'Rationale Quality',
    icon: Lightbulb,
    description: 'Decision context',
    criteria: {
      5: 'Full context preserved',
      4: 'Minor gaps',
      3: 'Partial rationale',
      2: 'Vague reasoning',
      1: 'No context',
    },
  },
  contextPreservation: {
    label: 'Context Preservation',
    icon: FileText,
    description: 'Future readability',
    criteria: {
      5: 'Future team fully understands',
      4: 'Most context captured',
      3: 'Basic understanding',
      2: 'Missing key info',
      1: 'Insufficient',
    },
  },
  alignmentLevel: {
    label: 'Alignment Level',
    icon: Users,
    description: 'Team sync',
    criteria: {
      5: 'Full alignment',
      4: 'Minor gaps',
      3: 'Some misalignment',
      2: 'Significant gaps',
      1: 'Major misalignment',
    },
  },
  actionClarity: {
    label: 'Action Clarity',
    icon: CheckCircle,
    description: 'Next steps defined',
    criteria: {
      5: 'All actions clear',
      4: 'Most actions defined',
      3: 'Some clarity',
      2: 'Vague actions',
      1: 'No clear actions',
    },
  },
}

// Get relevant scores based on source type
function getRelevantScores(sourceType: SourceType | undefined): (keyof SourceScores)[] {
  switch (sourceType) {
    case 'user_interview':
    case 'buyer_interview':
    case 'support_ticket':
      return ['momTestScore', 'evidenceStrength', 'contextRichness']
    case 'stakeholder_interview':
    case 'meeting_notes':
      return ['alignmentLevel', 'actionClarity', 'contextRichness']
    case 'document':
    case 'issue':
    case 'url':
      return ['evidenceStrength', 'contextRichness', 'rationaleQuality']
    default:
      return ['evidenceStrength', 'contextRichness']
  }
}

export function ScoreDisplay({ scores, sourceType }: ScoreDisplayProps) {
  // Get all scores that have values
  const allAvailableScores = Object.keys(scores).filter(
    (key) => scores[key as keyof SourceScores] !== undefined
  ) as (keyof SourceScores)[]

  // Prioritize based on source type, but show all that have values
  const priorityScores = getRelevantScores(sourceType)
  const scoresToShow = priorityScores.filter(s => scores[s] !== undefined)

  // Add any other scores that have values but weren't in priority list
  for (const score of allAvailableScores) {
    if (!scoresToShow.includes(score)) {
      scoresToShow.push(score)
    }
  }

  if (scoresToShow.length === 0) {
    return null
  }

  return (
    <div className="p-4 rounded-lg bg-bg-secondary">
      <h4 className="text-sm font-medium mb-3">Analysis Scores</h4>
      <div className="grid grid-cols-2 gap-4">
        {scoresToShow.map((scoreKey) => {
          const value = scores[scoreKey]
          const config = SCORE_CONFIG[scoreKey as keyof typeof SCORE_CONFIG]
          if (value === undefined || !config) return null

          const Icon = config.icon
          const criteriaText = config.criteria[value as keyof typeof config.criteria]

          return (
            <div key={scoreKey} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-text-muted" />
                <span className="text-sm font-medium">{config.label}</span>
              </div>

              {/* Score bar */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-[3px] bg-warm-300 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent-500 transition-all"
                    style={{ width: `${(value / 5) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold min-w-[1.5rem] text-right text-fg">
                  {value}
                </span>
              </div>

              {/* Criteria explanation */}
              <p className="text-xs text-text-muted">{criteriaText}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
